import React, { ReactNode, useEffect, useState, useContext } from 'react'
import { join } from 'lodash'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import MaskedInput from 'react-maskedinput'
import styled, { css } from 'styled-components'
import Select from 'live/components/forms/ui/select'
import i18n from 'live/services/i18n'
import theme from 'live/services/theme'
import { InputRadio, STATIC_DATA_STATES, UnitsTypes } from '../chart-components'
import {
    ModalField,
    CustomMeasureSelect,
    CustomDatePicker,
    ModalSectionHeader,
    IntegerInput,
    NumberInput
} from '../modal-components'
import { fetchEntityOptions, fetchUnitOptions, RUN_API } from '../../api/api'
import { timestampToDateTime, dateTimeToTimestamp, dateAndTimeRegex, regexNumberAndComma } from '../../utils/parse'
import { RawRunEvent, SectionOptionsTemplate, SelectOptions, Uom } from '../../utils/type'
import { RunErrors } from './types'
import { StaticDataContext } from '../../context/StaticDataContext'
import { componentExtractor, RunTabRegistryService } from '../../../../../shared/services/StaticDataManagerRegistry'
import { ExtensionPoint } from 'live/components/ui/ExtensionPoint'
import { Switch } from '@intelie/ui'

interface RunTabProps {
    event: RawRunEvent
    children?: ReactNode
    errors: RunErrors
    updateRunEvent: (event: RawRunEvent) => void
    hasPermissionToEdit: boolean
    isSharedComponent: boolean
    setIsSharedComponent: React.Dispatch<React.SetStateAction<boolean>>
    tubulars: any[]
    setTubulars: React.Dispatch<React.SetStateAction<any[]>>
    sharedState: Record<string, unknown>
    setSharedState: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    runModalContainer: React.MutableRefObject<HTMLDivElement>
    fieldDisabledAfterEntityStart: boolean
    unitOptions: Uom[]
    setUnitOptions: React.Dispatch<React.SetStateAction<Uom[]>>
    selectedEvent: RawRunEvent
    setSelectedEvent: React.Dispatch<React.SetStateAction<RawRunEvent>>
    isTransitionOrNotTubular: boolean
    cementedPlugPresent: boolean
    setCementedPlugPresent: React.Dispatch<React.SetStateAction<boolean>>
    addNewCementedPlug: () => void
    isModalDoubleSize: boolean
    setIsModalDoubleSize: React.Dispatch<React.SetStateAction<boolean>>
    emptyCementedPlug: { startMD?: ValueUom; finalMD?: ValueUom }
}

const RunTab: React.FC<RunTabProps> = ({
    event,
    errors,
    updateRunEvent,
    hasPermissionToEdit,
    isSharedComponent,
    setIsSharedComponent,
    tubulars,
    setTubulars,
    sharedState,
    setSharedState,
    runModalContainer,
    fieldDisabledAfterEntityStart,
    unitOptions,
    setUnitOptions,
    selectedEvent,
    setSelectedEvent,
    isTransitionOrNotTubular,
    cementedPlugPresent,
    setCementedPlugPresent,
    addNewCementedPlug,
    isModalDoubleSize,
    setIsModalDoubleSize,
    emptyCementedPlug
}: RunTabProps) => {
    const eventStartDateTime = event ? timestampToDateTime(event.start) : null
    const eventEndDateTime = event ? timestampToDateTime(event.end) : null
    const [startDate, setStartDate] = useState(eventStartDateTime?.date || null)
    const [startHour, setStartHour] = useState(eventStartDateTime?.time || null)
    const [endDate, setEndDate] = useState(eventEndDateTime?.date || null)
    const [endHour, setEndHour] = useState(eventEndDateTime?.time || null)
    const [typeList, setTypeList] = useState([])
    const [tubularInterval, setTubularInterval] = useState<{
        tubularsIntervalPerTime: boolean
        tubularsIntervalPerDepth: boolean
    } | null>(null)

    const context = useContext(StaticDataContext)

    const handleTubularIntervalChange = (e): void => {
        const name = e?.target?.name

        const newTubularInterval = {
            tubularsIntervalPerTime: name === 'tubularsIntervalPerTime',
            tubularsIntervalPerDepth: name === 'tubularsIntervalPerDepth'
        }

        setTubularInterval(newTubularInterval)
        setSelectedEvent(prevstate => ({
            ...prevstate,
            ...newTubularInterval
        }))
    }

    useEffect(() => {
        if (fieldDisabledAfterEntityStart) {
            const newIntervalValues = {
                tubularsIntervalPerTime: selectedEvent?.tubularsIntervalPerTime,
                tubularsIntervalPerDepth:
                    selectedEvent?.tubularsIntervalPerDepth || !selectedEvent?.tubularsIntervalPerTime
            }
            setTubularInterval(newIntervalValues)
            updateRunEvent({
                ...selectedEvent,
                ...newIntervalValues
            })
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        updateRunEvent(selectedEvent)
    }, [selectedEvent, updateRunEvent])

    useEffect(() => {
        setSelectedEvent(prevstate => ({ ...prevstate, start: event?.start, number: event?.number }))
        if (eventStartDateTime?.date !== startDate || eventStartDateTime?.time !== startHour) {
            setStartDate(eventStartDateTime?.date)
            setStartHour(eventStartDateTime?.time)
        }
    }, [eventStartDateTime?.date, eventStartDateTime?.time, event?.number]) // TODO: verify dependencies to avoid loop

    useEffect(() => {
        fetchEntityOptions(RUN_API).then((e: SectionOptionsTemplate | null) => {
            if (!e) return
            setTypeList(e.type as SelectOptions[])
        })
        fetchUnitOptions(UnitsTypes.LENGTH).then(r => {
            if (!r) return
            setUnitOptions(r.options)
            const defaultUnit = r.options.find(elem => elem.name == 'm') || r[0]
            setSelectedEvent(prevstate => ({
                ...prevstate,
                startMD: {
                    value: prevstate.startMD?.value,
                    uom: prevstate.startMD?.uom || defaultUnit
                },
                finalMD: {
                    value: prevstate.finalMD?.value,
                    uom: prevstate.finalMD?.uom || defaultUnit
                }
            }))
        })
    }, [])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([startDate, startHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([startDate, startHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, start: timestamp }))
        }
    }, [startDate, startHour])

    useEffect(() => {
        if (dateAndTimeRegex.test(join([endDate, endHour], ' '))) {
            const timestamp = dateTimeToTimestamp(join([endDate, endHour], ' '))
            setSelectedEvent(prevstate => ({ ...prevstate, end: timestamp }))
        }
    }, [endDate, endHour])

    const props = {
        runRawEvent: event,
        errors,
        updateRunEvent,
        hasPermissionToEdit,
        isSharedComponent,
        setIsSharedComponent,
        tubulars,
        setTubulars,
        sharedState,
        setSharedState,
        runModalContainer: runModalContainer?.current,
        selectedEvent,
        setSelectedEvent,
        eventStartDateTime,
        eventEndDateTime,
        startDate,
        setStartDate,
        startHour,
        setStartHour,
        endDate,
        setEndDate,
        endHour,
        setEndHour,
        typeList,
        setTypeList,
        unitOptions,
        setUnitOptions,
        fieldDisabledAfterEntityStart,
        context,
        isTransitionOrNotTubular,
        cementedPlugPresent,
        setCementedPlugPresent,
        addNewCementedPlug,
        isModalDoubleSize,
        setIsModalDoubleSize,
        emptyCementedPlug
    }

    const cementedPlugs = cementedPlugPresent
        ? selectedEvent?.cementedPlugs
            ? selectedEvent?.cementedPlugs
            : [emptyCementedPlug]
        : [emptyCementedPlug]

    return (
        <div onMouseDown={e => e.stopPropagation()}>
            <ModalSectionHeader label={i18n('Attributes')} />
            <ModalField
                label={'Run Number'}
                labelAditionalField={
                    <OverlayTrigger
                        placement="top"
                        overlay={
                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                {i18n(
                                    'The value of this field is automatically filled in corresponding to the sum of the value of the previous run (Number of the previous run +1) in the same section, obeying the increasing sequence of runs. For example, if the previous run was run 4, the value of this field would be 5, and so on.'
                                )}
                            </Tooltip>
                        }
                    >
                        <i
                            style={{
                                color: theme['link-color'],
                                cursor: 'default',
                                marginLeft: '8px',
                                paddingBottom: '2px'
                            }}
                            className="far fa-info-circle"
                        />
                    </OverlayTrigger>
                }
                field={
                    <div className="input-normal">
                        {selectedEvent?.number && (
                            <IntegerInput
                                disabled
                                value={selectedEvent.number}
                                style={errors?.number ? { borderColor: '#D9534F' } : {}}
                            />
                        )}
                    </div>
                }
            />
            {!isTransitionOrNotTubular && (
                <FlexContainer>
                    <ModalField
                        label={'Start MD'}
                        labelAditionalField={
                            <CustomMeasureWithTooltip>
                                <CustomMeasureSelect
                                    disabled
                                    setSelectedUomValue={uomValue =>
                                        setSelectedEvent(prevstate => ({ ...prevstate, startMD: uomValue }))
                                    }
                                    selectedOptionValues={selectedEvent.startMD}
                                    options={unitOptions}
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip style={{ zIndex: 9999 }} id={''}>
                                            {i18n(
                                                'You must fill this field with a value greater than the MD Final value from the previous run. ' +
                                                    'Fill this field with a value greater than the MD Final value from the previous run.'
                                            )}
                                        </Tooltip>
                                    }
                                >
                                    <i
                                        style={{
                                            color: theme['link-color'],
                                            cursor: 'default',
                                            marginLeft: '8px',
                                            paddingBottom: '2px'
                                        }}
                                        className="far fa-info-circle"
                                    />
                                </OverlayTrigger>
                            </CustomMeasureWithTooltip>
                        }
                        field={
                            <div className="input-uom">
                                <NumberInput
                                    disabled={
                                        !hasPermissionToEdit ||
                                        isTransitionOrNotTubular ||
                                        fieldDisabledAfterEntityStart
                                    }
                                    value={selectedEvent.startMD?.value ?? ''}
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                startMD: {
                                                    uom: selectedEvent.startMD?.uom,
                                                    value: Number(e?.target?.value)
                                                }
                                            }))
                                        }
                                        if (e?.target?.value === '') {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                startMD: {
                                                    uom: selectedEvent.startMD?.uom,
                                                    value: null
                                                }
                                            }))
                                        }
                                    }}
                                    style={errors?.startMD ? { borderColor: '#D9534F' } : {}}
                                    placeholder={`0.00`}
                                />
                            </div>
                        }
                    />
                    <ModalField
                        label={'Final MD'}
                        labelAditionalField={
                            <CustomMeasureWithTooltip>
                                <CustomMeasureSelect
                                    disabled
                                    setSelectedUomValue={uomValue =>
                                        setSelectedEvent(prevstate => ({ ...prevstate, finalMD: uomValue }))
                                    }
                                    selectedOptionValues={selectedEvent.finalMD}
                                    options={unitOptions}
                                />
                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip style={{ zIndex: 9999 }} id={''}>
                                            {i18n(
                                                'You must fill this field with a value less than or equal to Section Final MD and greater than Run Start MD.'
                                            )}
                                        </Tooltip>
                                    }
                                >
                                    <i
                                        style={{
                                            color: theme['link-color'],
                                            cursor: 'default',
                                            marginLeft: '8px',
                                            paddingBottom: '2px'
                                        }}
                                        className="far fa-info-circle"
                                    />
                                </OverlayTrigger>
                            </CustomMeasureWithTooltip>
                        }
                        field={
                            <label className="input-uom">
                                <NumberInput
                                    disabled={
                                        !hasPermissionToEdit ||
                                        isTransitionOrNotTubular ||
                                        selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                    }
                                    value={selectedEvent.finalMD?.value ?? ''}
                                    onChange={e => {
                                        if (regexNumberAndComma.test(e?.target?.value)) {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                finalMD: {
                                                    uom: selectedEvent.finalMD?.uom,
                                                    value: Number(e?.target?.value)
                                                }
                                            }))
                                        }
                                        if (e?.target?.value === '') {
                                            e.persist()
                                            setSelectedEvent(prevstate => ({
                                                ...prevstate,
                                                finalMD: {
                                                    uom: selectedEvent.finalMD?.uom,
                                                    value: null
                                                }
                                            }))
                                        }
                                    }}
                                    style={errors?.finalMD ? { borderColor: '#D9534F' } : {}}
                                    placeholder={`0.00`}
                                />
                            </label>
                        }
                    />
                </FlexContainer>
            )}
            <ModalField
                style={{ margin: 0 }}
                field={
                    <div className="input-date-time">
                        <ModalField
                            label={'Start Date'}
                            field={
                                <CustomDatePicker
                                    isDisabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                    date={startDate}
                                    setDate={setStartDate}
                                />
                            }
                        />
                        <ModalField
                            label={'Start Hour'}
                            field={
                                <MaskedInput
                                    disabled={!hasPermissionToEdit || fieldDisabledAfterEntityStart}
                                    className={'masked-input'}
                                    style={errors?.start ? { borderColor: '#D9534F' } : {}}
                                    mask="11:11"
                                    type="text"
                                    placeholder={'00:00'}
                                    value={startHour}
                                    onChange={e => setStartHour(e.target.value)}
                                />
                            }
                        />
                    </div>
                }
                enabled={false}
            />
            {selectedEvent.state != STATIC_DATA_STATES.EMPTY && (
                <ModalField
                    style={{ margin: 0 }}
                    field={
                        <div className="input-date-time">
                            <ModalField
                                label={'End Date'}
                                field={
                                    <CustomDatePicker
                                        isDisabled={
                                            !hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                        }
                                        date={eventEndDateTime?.date}
                                        setDate={setEndDate}
                                    />
                                }
                            />
                            <ModalField
                                label={'End Hour'}
                                field={
                                    <MaskedInput
                                        disabled={
                                            !hasPermissionToEdit || selectedEvent.state === STATIC_DATA_STATES.FINISHED
                                        }
                                        className={'masked-input'}
                                        style={errors?.end ? { borderColor: '#D9534F' } : {}}
                                        mask="11:11"
                                        type="text"
                                        placeholder={'00:00'}
                                        value={eventEndDateTime?.time}
                                        onChange={e => setEndHour(e.target.value)}
                                    />
                                }
                            />
                        </div>
                    }
                />
            )}
            <ModalField
                label={'Type'}
                field={
                    <>
                        <Select
                            searchable={false}
                            clearable={false}
                            options={typeList}
                            value={selectedEvent?.type ?? ''}
                            placeholder={selectedEvent.type ? `${selectedEvent.type}` : i18n('Select type')}
                            onChange={value => setSelectedEvent(prevstate => ({ ...prevstate, type: value }))}
                            style={errors?.type ? { borderColor: '#D9534F' } : {}}
                            disabled={!hasPermissionToEdit && isTransitionOrNotTubular && fieldDisabledAfterEntityStart}
                        />
                    </>
                }
            />
            <CheckBoxContainer>
                <ModalField
                    field={
                        <SectionWithSeparator showSeparatorTop showSeparatorBottom>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                                <ModalSectionHeader label={i18n('TUBULARS INTERVAL')} />
                                <CustomMeasureWithTooltip>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                                {i18n(
                                                    'For insertion of tubulars by time, enable the checkbox. ' +
                                                        'The default behavior is the insertion of tubulars by  Depth (checkbox disabled). ' +
                                                        'Once the Run is started,  the tubulars in the workstring must be configured with the tubular interval type defined here.'
                                                )}
                                            </Tooltip>
                                        }
                                    >
                                        <i
                                            style={{
                                                color: theme['link-color'],
                                                cursor: 'default',
                                                marginLeft: '4px',
                                                paddingBottom: '2px'
                                            }}
                                            className="far fa-info-circle"
                                        />
                                    </OverlayTrigger>
                                </CustomMeasureWithTooltip>
                            </div>

                            <div
                                className="input-checkbox"
                                style={errors?.tubularsInterval ? { border: '1px solid #D9534F', padding: '8px' } : {}}
                            >
                                <InputRadio
                                    name="tubularsIntervalPerDepth"
                                    labelRight={i18n('Per depth')}
                                    checked={isTransitionOrNotTubular || tubularInterval?.tubularsIntervalPerDepth}
                                    showRadioWhenDisabled={tubularInterval?.tubularsIntervalPerDepth}
                                    disabled={
                                        !hasPermissionToEdit ||
                                        fieldDisabledAfterEntityStart ||
                                        isTransitionOrNotTubular
                                    }
                                    onChange={handleTubularIntervalChange}
                                />
                                <InputRadio
                                    name="tubularsIntervalPerTime"
                                    labelRight={i18n('Per time')}
                                    style={{ marginLeft: '8px' }}
                                    checked={isTransitionOrNotTubular || tubularInterval?.tubularsIntervalPerTime}
                                    showRadioWhenDisabled={tubularInterval?.tubularsIntervalPerTime}
                                    disabled={
                                        !hasPermissionToEdit ||
                                        fieldDisabledAfterEntityStart ||
                                        isTransitionOrNotTubular
                                    }
                                    onChange={handleTubularIntervalChange}
                                />
                            </div>

                            <ExtensionPoint
                                componentProps={props}
                                strategy={'render-all'}
                                registry={RunTabRegistryService.registry}
                                componentExtractor={componentExtractor}
                            />
                        </SectionWithSeparator>
                    }
                />
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative'
                    }}
                >
                    <div>
                        <ModalField
                            field={
                                <CheckBoxContainer>
                                    <ModalField
                                        field={
                                            <div className="input-checkbox" style={{ paddingTop: 16 }}>
                                                <Switch
                                                    active={!!cementedPlugPresent}
                                                    label={i18n('Cemented Plug Present')}
                                                    labelPosition="right"
                                                    disabled={
                                                        !hasPermissionToEdit ||
                                                        fieldDisabledAfterEntityStart ||
                                                        isTransitionOrNotTubular
                                                    }
                                                    onChange={() => {
                                                        cementedPlugPresent
                                                            ? setSelectedEvent(prev => ({
                                                                  ...prev,
                                                                  cementedPlugs: []
                                                              }))
                                                            : addNewCementedPlug()
                                                        setCementedPlugPresent(prev => !prev)
                                                        setIsModalDoubleSize(!cementedPlugPresent)
                                                    }}
                                                />

                                                <CustomMeasureWithTooltip>
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip style={{ zIndex: 9999 }} id={''}>
                                                                {i18n(
                                                                    'When enabled, the KPIs of this Run will be classified as Cemented Plug Present, for Stand Type Inclusion (trip in) only. ' +
                                                                        'The Cement Start MD and Cement Final MD fields will be enabled for entering numerical values contained between the Run Start MD and Run Final MD values.'
                                                                )}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <i
                                                            style={{
                                                                color: theme['link-color'],
                                                                cursor: 'default'
                                                            }}
                                                            className="far fa-info-circle"
                                                        />
                                                    </OverlayTrigger>
                                                </CustomMeasureWithTooltip>
                                            </div>
                                        }
                                    />
                                </CheckBoxContainer>
                            }
                        />

                        <div style={{ marginTop: 16 }}>
                            {cementedPlugs?.map((plug, index) => {
                                return (
                                    <div
                                        key={`plug-${index}`}
                                        style={{ display: 'flex', flexDirection: 'column', marginBottom: 8 }}
                                    >
                                        <div style={{ alignItems: 'center' }}>
                                            <CementedPlugListItem>
                                                <span>Start MD:</span>{' '}
                                                <span>
                                                    {plug.startMD?.value ?? '0.00'}
                                                    {plug.startMD?.uom?.name}
                                                </span>
                                            </CementedPlugListItem>
                                            {' - '}
                                            <CementedPlugListItem>
                                                <span>Final MD:</span>{' '}
                                                <span>
                                                    {plug.finalMD?.value ?? '0.00'}
                                                    {plug.finalMD?.uom?.name}
                                                </span>
                                            </CementedPlugListItem>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {cementedPlugPresent && (
                        <ResizeButton onClick={() => setIsModalDoubleSize(!isModalDoubleSize)}>
                            <i
                                className={'far fa-chevron-' + (isModalDoubleSize ? 'left' : 'right')}
                                style={{ color: '#37A3EF' }}
                            />
                        </ResizeButton>
                    )}
                </div>
            </CheckBoxContainer>
        </div>
    )
}

const ResizeButton = styled.div`
    position: absolute;
    top: 0;
    right: -20px;
    width: 6em;
    bottom: 0;
    height: calc(100% + 20px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    padding-right: 16px;
    background: var(--gray-0);
    cursor: pointer;

    i {
        color: var(--blue-500);
        text-align: center;
        font-size: 14px;
        font-style: normal;
        font-weight: 900;
        line-height: 16px;
    }
`

const CementedPlugListItem = styled.span`
    color: var(--gray-800);
    font-family: 'Source Sans Pro';
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 14px;

    span:first-child {
        font-weight: 600;
    }
`

const CustomMeasureWithTooltip = styled.div`
    display: flex;
    align-items: center;
    margin-left: 0.25rem;
`

const FlexContainer = styled.div`
    display: flex;
    align-items: baseline;
    gap: 1rem;
`

const CheckBoxContainer = styled.div`
    .input-checkbox {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
    }

    .input-checkbox input[type='checkbox']:not(.browser-default-input-style):checked::after {
        color: white !important;
        background-color: var(--blue-500);
    }
    label {
        padding: 0;
        color: var(--gray-800);
        font-family: 'Source Sans Pro';
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px;
    }
`

const SectionWithSeparator = styled.div<{ showSeparatorTop?: boolean; showSeparatorBottom?: boolean }>`
    position: relative;
    margin-top: 4px;
    padding: 16px 0px;

    &::before,
    &::after {
        content: '';
        height: 4px;
        left: -20px;
        right: -20px;
        position: absolute;
        width: calc(100% + 40px);
    }

    ${({ showSeparatorTop }) =>
        showSeparatorTop &&
        css`
            &::before {
                top: 0px;
                border-top: 1px solid var(--gray-300);
            }
        `}

    ${({ showSeparatorBottom }) =>
        showSeparatorBottom &&
        css`
            &::after {
                bottom: 0px;
                border-bottom: 1px solid var(--gray-300);
            }
        `}
`
export default RunTab
